import { OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { RoutinesService } from "../../../services/routines.service";
import { MyToasterService } from "../../../services/my-toaster.service";
import { LoadingService } from "../../../services/loading.service";

export abstract class BaseFormComponent implements OnInit {
  // Rotina que este controller trabalha
  public routine: string;

  // Nome da rotina
  public rotina: string = "";
  public classificacao: string = "";

  // Indica se o formulário houve uma submissão/tentativa de submissão do form
  public submitted: boolean = false;
  public submitting: boolean = false;

  // Variável para verificar se precisa verificar o dado ou não
  public checkHasChange: boolean = true;

  // Objeto que armazena os erros recebidos da API
  public erros: any = {};

  // Variável base para o formulário, usada para limpar o formulário
  public clearData: any = {};

  // Variável para manipulação dos dados
  public dados: any = {};

  // Variável para veririficar se o formulário precisa ser submetido.
  public dadosOriginais: any = {};

  public hasInputError: boolean = false;

  public isNewRegister: boolean = false;

  constructor(
    public myToaster: MyToasterService,
    public route: ActivatedRoute,
    public routinesService: RoutinesService,
    public loadingService: LoadingService
  ) {}

  async ngOnInit() {
    try {
      this.dados = Object.assign({}, this.clearData);
      this.dadosOriginais = Object.assign({}, this.clearData);

      //Espera a a busca de rotinas ser concluída
      await this.routinesService.buscaRealizada;

      //Recebe a rotina referente a este controller
      if (this.routinesService.rotinas[this.routine])
        this.rotina = this.routinesService.rotinas[this.routine].Rotina;

      this.classificacao = this.routinesService.retornaClassificacao(
        this.routine
      );

      //Recebe o id do registro dos campos ou não
      this.dados.id = +(this.route.snapshot.params["id"] || 0);

      //Faz uma requisição à API para preencher o formulário
      await this._init();

      //Capta os erros
    } catch (error) {
      //Limpa o id
      this.dados.id = 0;

      //Exibe o erro
      this.myToaster.showGetErrorToast(error);
    }
  }

  /**
   * _init
   *
   * Método buscar os dados e preencher o formulário
   *
   * @protected
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  {void}
   * @return {void}
   */
  protected abstract _init();

  /**
   * _save
   *
   * Método para realizar o salvar
   *
   * @protected
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  {void}
   * @return {void}
   */
  protected abstract _save();

  /**
   * _hasChanges
   *
   * Método para verificar a existência de alterações no formulário
   *
   * @protected
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  {void}
   * @return {boolean}
   */
  protected _hasChanges() {
    //Verifica se não há id
    if (!this.dados.id)
      //Retorna que há alterações, já que um novo registro pode ser salvo
      return true;

    //Percorre cada campo
    for (let key in this.dados) {
      //Procura uma alteração
      if (this.dados[key] != this.dadosOriginais[key])
        //se encontrar, retrona true
        return true;
    }
    //se não houver alterações, retorna falso
    return false;
  }

  /**
   * _baseRegisterInit
   *
   * Método básico do init do registro podendo ser chamado na implementação do init
   *
   * @protected
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  {object}
   * @return {void}
   */
  protected _baseRegisterInit(registro) {
    //Se vier dados
    if (registro) {
      //Percorre cada campo
      for (let key in this.dados) {
        //Verifica se o campo foi recebido
        if (registro[key]) {
          //Atribui o valor do campo à variavel de manipulação e de verificação de alterações
          this.dados[key] = registro[key];
          this.dadosOriginais[key] = registro[key];
        }
      }
    }
    // limpa o id
    else this.dados.id = 0;

    // Atualiza o id na variavel de verificação
    this.dadosOriginais.id = this.dados.id;
  }

  /**
   * newRegister
   *
   * Método para limpar o formulário e zerar os erros
   *
   * @protected
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  {void}
   * @return {void}
   */
  public newRegister() {
    //Limpa os dados
    this.dados = Object.assign(this.dados, this.clearData);
    this.dadosOriginais = Object.assign(this.dadosOriginais, this.clearData);

    this.isNewRegister = true;
    this.submitted = false;
    this.erros = {};
  }

  /**
   * onSubmit
   *
   * Método chamado na tentativa de submissão do formulário
   *
   * @public
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  {object}
   * @return {void}
   */
  public async onSubmit(form) {
    //Atualiza o controle de submissão
    this.submitted = true;
    this.submitting = true;
    this.erros = {};

    //Verifica se o formulário é válido
    if (form.valid) {
      //Verifica se há alterações
      if (!this._hasChanges() && this.checkHasChange) {
        //Libera a nova submissão
        this.submitting = false;

        //Informa que foi sucesso
        this.onSaveSuccess(false);
      }
      //Se não houver alterações
      else {
        try {
          await this._save();

          //Libera a nova submissão
          this.submitting = false;

          //Informa que foi sucesso
          this.onSaveSuccess(true);

          if (this.hasInputError) {
            this.hasInputError = false;
          }

          //Captura os erros
        } catch (error) {
          //Libera a nova submissão
          this.submitting = false;

          //Se for um objeto
          if (typeof error === "object") {
            //Recebe os erros
            this.erros = error;

            //Se houver um erro geral
            if (error.Geral)
              //Exibe uma mensagem com o erro
              this.myToaster.showSaveErrorToast(error.Geral);
            //Senão, o erro é exibido nos respectivos campos
            else {
              this.myToaster.showCompletionToast();
              this.hasInputError = true;
            }
          }

          //Senão e se houver erro
          else if (error)
            //Mostra o erro
            this.myToaster.showSaveErrorToast(error);
          //Senão, mostra uma mensagem padrão
          else
            this.myToaster.showErrorToast("Não foi possível salvar os dados");
        }
      }
    } else {
      //Mostra a mensagem sobre erros de preenchimento
      this.myToaster.showCompletionToast();
      this.hasInputError = true;
      //Libera a nova submissão
      this.submitting = false;
    }
  }

  /**
   * onSaveSuccess
   *
   * Método para tratar o sucesso no save recebendo
   * como parâmetro se a requisição foi realizada
   *
   * @protected
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  {boolean}
   * @return {void}
   */
  public onSaveSuccess(doRequest: boolean) {
    //Informa que foi sucesso
    this.myToaster.showSaveSuccessToast();
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/_core/base-form.component.ts